import React, { useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getTheme } from '../../theme';
import Button from '../../components/common/Button';
import { api } from '../../services/api';
import { Close, CloudUpload } from '@mui/icons-material';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.lg};
  padding: ${props => getTheme(props.theme?.isDark).spacing.xl};
  width: 90%;
  max-width: 600px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  cursor: pointer;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  
  &:hover {
    background-color: ${props => getTheme(props.theme?.isDark).colors.hover};
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const FileInput = styled.input`
  width: 100%;
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surfaceElevated};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
  }
`;

const FileInfo = styled.div`
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surfaceElevated};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  font-size: 14px;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin-top: ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 14px;
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  background-color: #fee2e2;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const AppVersionUploadModal = ({ isOpen, onClose, version, onSuccess }) => {
  const { isDark } = useTheme();
  const theme = { isDark };

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type based on platform
      const validExtensions = version.platform === 'ios' 
        ? ['.ipa'] 
        : ['.apk', '.aab'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!validExtensions.includes(fileExtension)) {
        setError(`Yalnız ${validExtensions.join(', ')} faylları yüklənə bilər`);
        setSelectedFile(null);
        return;
      }

      // Validate file size (max 500MB)
      const maxSize = 500 * 1024 * 1024; // 500MB
      if (file.size > maxSize) {
        setError('Fayl ölçüsü 500MB-dan böyük ola bilməz');
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedFile) {
      setError('Fayl seçilməlidir');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('appFile', selectedFile);
      formData.append('platform', version.platform);
      formData.append('latestVersion', version.latestVersion);
      formData.append('minRequiredVersion', version.minRequiredVersion);
      formData.append('forceUpdate', version.forceUpdate);
      formData.append('updateMessage', version.updateMessage);
      if (version.appStoreUrl) formData.append('appStoreUrl', version.appStoreUrl);
      if (version.playStoreUrl) formData.append('playStoreUrl', version.playStoreUrl);

      await api.updateAppVersion(version._id || version.id, formData);

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Fayl yüklənmədi');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
        <ModalHeader theme={theme}>
          <ModalTitle theme={theme}>
            App Faylını Yüklə - {version.platform} v{version.latestVersion}
          </ModalTitle>
          <CloseButton theme={theme} onClick={onClose}>
            <Close />
          </CloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          {error && <ErrorText theme={theme}>{error}</ErrorText>}

          <FormGroup theme={theme}>
            <Label theme={theme}>
              App Faylı ({version.platform === 'ios' ? 'IPA' : 'APK/AAB'})
            </Label>
            <FileInput
              theme={theme}
              type="file"
              accept={version.platform === 'ios' ? '.ipa' : '.apk,.aab'}
              onChange={handleFileChange}
              required
            />
            {selectedFile && (
              <FileInfo theme={theme}>
                <div><strong>Fayl:</strong> {selectedFile.name}</div>
                <div><strong>Ölçü:</strong> {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</div>
              </FileInfo>
            )}
          </FormGroup>

          <div style={{ display: 'flex', gap: getTheme(isDark).spacing.sm, marginTop: getTheme(isDark).spacing.lg }}>
            <Button
              type="submit"
              variant="primary"
              theme={theme}
              style={{ flex: 1 }}
              disabled={uploading}
            >
              <CloudUpload /> {uploading ? 'Yüklənir...' : 'Yüklə'}
            </Button>
            <Button
              type="button"
              variant="outline"
              theme={theme}
              onClick={onClose}
              disabled={uploading}
            >
              Ləğv et
            </Button>
          </div>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AppVersionUploadModal;

